import {Title} from "./base/Title";
import React, {useEffect, useRef, useState} from "react";
import {Dialog} from "./base/Dialog";
import {getPreparerLivraison, PreparerLivraisonData} from "../services/apiLivraisons";
import {Button} from "./base/Button";
import { handleApiError } from "utils/errorHandlers";
import { ApiError } from "./ApiError";

interface DialogPreparerLivraisonProps {
	open: boolean;
	onClose: () => void;
	livraisonId: number | null;
	livraisonNote: string | null;
}

export const DialogPreparerLivraison: React.FC<DialogPreparerLivraisonProps> = ({open, onClose, livraisonId, livraisonNote}) => {
	const [data, setData] = useState<PreparerLivraisonData>();
	const [loading, setLoading] = useState(false);
	const tableRef = useRef<HTMLTableElement>(null);
	const [errors, setErrors] = useState<Record<string, string[]>>({});

	useEffect(() => {
		if (open && livraisonId) {
			const fetchData = async () => {
				setLoading(true);
				try {
					const data = await getPreparerLivraison(livraisonId);
					setData(data);
				} catch (err) {
					setErrors(handleApiError(err));
				} finally {
					setLoading(false);
				}
			};
			fetchData();
		}
	}, [open, livraisonId]);

	const produits = data ? Object.keys(data) : [];
	const utilisateurs = data && produits ? Array.from(
		new Set(
			produits.flatMap((produit) => Object.keys(data[produit]))
		)
	) : [];
	const totals = data && produits ? produits.map(
		(produit) => Object.values(data[produit]).reduce((acc, val) => acc + val, 0)
	) : [];

	const printTableau = () => {
		if (!tableRef.current) return;

		const printContent = tableRef.current.outerHTML;
		const printWindow = window.open("", "_blank", "width=800,height=600");
		if (printWindow !== null) {
			printWindow?.document.write(`
      <html lang="fr">
        <head>
          <title>Détail de la livraison #${livraisonId}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #E1E1E1; padding: 6px; text-align: center; }
            th { background-color: #f5f5f5; }
            .total { font-weight: bold; background: #eee; }
          </style>
        </head>
        <body>
          ${printContent}
          <script>
            window.print();
            window.close();
          </script>
        </body>
      </html>
    `);
			printWindow.document.close();
		}
	}

	return (
		<Dialog
			open={open}
			width={60}
			onClose={onClose}
			title={`Détail de la livraison #${livraisonId}`}
			customBtn
		>
			{livraisonNote && (
				<>
					<p className="text-sm text-gray-500">{livraisonNote}</p>
				</>
			)}
			<>
				{loading ? (
					<div className="text-center py-4">Chargement des données...</div>
				) : errors && errors.general ? (
					<ApiError>{errors.general[0]}</ApiError>
				) : (
					<table ref={tableRef} className="border w-full">
						<thead>
						<tr className="border-b bg-neutral-100">
							<th className="p-2">Nom \\ Produit</th>
							{produits.map((produit) => (
								<th key={produit} className="p-2">
									{produit}
								</th>
							))}
						</tr>
						</thead>
						<tbody>
						{utilisateurs.map((utilisateur) => (
							<tr key={utilisateur} className="border-b bg-neutral-50 odd:brightness-90">
								<td className="p-2 font-semibold">
									{utilisateur}
								</td>
								{produits.map((produit) => (
									<td key={produit} className="p-2 text-center">
										{data ? data[produit][utilisateur] ?? 0 : 0}
									</td>
								))}
							</tr>
						))}
						<tr key="total" className="border-b bg-neutral-50 odd:brightness-90">
							<td className="p-2 font-semibold">TOTAL</td>
							{totals.map((total, idx) => (
								<td key={idx} className="p-2 text-center">
									{total}
								</td>
							))}
						</tr>
						</tbody>
					</table>
				)}
				<div className='flex justify-end gap-2 mt-6'>
					<Button onClick={onClose} variant="text">Fermer</Button>
					{utilisateurs.length > 0 && produits.length > 0 && (
						<Button onClick={() => printTableau()}>Imprimer</Button>
					)}
				</div>
			</>
		</Dialog>
	)
}